import { useState } from 'react';
import { TLocale } from '@aviv-estimation-funnel/shells/src/app/helpers/i18n';
import { Box } from '@gemini/core';
import { ChevronDownIcon, ChevronUpIcon } from '@gemini/icons';
import { Button } from '@gemini/ui';
import formatNumberToCurrency from '@mfe-helpers/formatNumberToCurrency';
import { default as downArrowIcon } from '@mfe-presentation/assets/svg/down-arrow.svg';
import { default as upArrowIcon } from '@mfe-presentation/assets/svg/up-arrow.svg';
import ReliabilityScore from '@mfe-presentation/components/ReliabilityScore';
import { useAppContext } from '@mfe-presentation/contexts/appContext';
import { TStyleProps } from '@mfe-types/box';
import { useLocale, useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { correctBrandName } from '@mfe-helpers/portalHelpers';

const priceStyles = (isCompactMode = false): TStyleProps => ({
    color: 'color.content.default.default',
    display: 'flex',
    flexDirection: {
        'breakpoint.base': 'column',
        'breakpoint.sm': isCompactMode ? 'column' : 'row',
    },
    marginHorizontal: {
        'breakpoint.base': 'spacing.8',
        'breakpoint.md': 'spacing.16',
    },
});

const PropertyValuation = ({
    type,
    isCompactMode = false,
}: {
    type: 'sell' | 'rent';
    isCompactMode: boolean;
}) => {
    const [isDescriptionVisible, setIsDescriptionVisible] = useState(false);
    const { estimations, portal } = useAppContext();
    const { t } = useTranslation(NAME_SPACE);
    const locale = useLocale();

    const lang = locale as unknown as TLocale;
    const { sellPrice, sellMinPrice, sellMaxPrice, sellPriceAccuracy } = estimations.valuation;
    const { rentPrice, rentMinPrice, rentMaxPrice, rentPriceAccuracy } = estimations.valuation;
    const price = formatNumberToCurrency(type === 'rent' ? rentPrice : sellPrice, lang);
    const minPrice = formatNumberToCurrency(type === 'rent' ? rentMinPrice : sellMinPrice, lang);
    const maxPrice = formatNumberToCurrency(type === 'rent' ? rentMaxPrice : sellMaxPrice, lang);
    const priceAccuracy = type === 'rent' ? rentPriceAccuracy : sellPriceAccuracy;

    const handleClickSwitchButton = () => {
        setIsDescriptionVisible((previousIsDescriptionVisible) => !previousIsDescriptionVisible);
    };

    return (
        <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            paddingHorizontal="spacing.16"
            paddingTop="spacing.16"
        >
            <Box typography="typography.body.16.bold" color="color.content.default.default">
                {t(`step_results.resultCard.title.${type}`)}
                {type === 'rent' && '*'}
            </Box>
            <Box typography="typography.headline.32.bold" display="flex" testId="average-price">
                {price}
            </Box>
            <Box typography="typography.body.14.regular" color="color.content.default.default">
                {t(`step_results.resultCard.subtitle.${type}`)}
            </Box>

            <Box display="flex" marginTop="spacing.8" alignItems="start">
                <Box display="flex" alignItems="start">
                    <Box {...priceStyles(isCompactMode)} alignItems="end" textAlign="right">
                        <Box typography="typography.body.14.regular">
                            {t('step_results.resultCard.lowestPrice')}
                        </Box>
                        <Box paddingLeft="spacing.6" typography="typography.body.14.regular">
                            {minPrice}
                        </Box>
                    </Box>
                    <Box paddingRight="spacing.8">
                        <img alt="down-arrow" src={downArrowIcon} />
                    </Box>
                </Box>
                <Box
                    height={{
                        'breakpoint.base': 'sizing.40',
                        'breakpoint.sm': isCompactMode ? 'sizing.40' : 'sizing.20',
                    }}
                    borderWidth="borderWidth.1"
                    borderStyle="solid"
                    borderColor="color.border.light.default"
                />
                <Box display="flex" alignItems="start">
                    <Box paddingLeft="spacing.8">
                        <img alt="up-arrow" src={upArrowIcon} />
                    </Box>
                    <Box {...priceStyles(isCompactMode)} alignItems="start">
                        <Box
                            typography="typography.body.14.regular"
                            paddingRight="spacing.6"
                            textAlign="left"
                        >
                            {t('step_results.resultCard.highestPrice')}
                        </Box>
                        <Box typography="typography.body.14.regular">{maxPrice}</Box>
                    </Box>
                </Box>
            </Box>
            <Box
                width="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="space-between"
            >
                {!!priceAccuracy && (
                    <Box marginTop="spacing.8">
                        <ReliabilityScore rating={priceAccuracy} />
                    </Box>
                )}
                <Button
                    onPress={handleClickSwitchButton}
                    icon={isDescriptionVisible ? ChevronUpIcon : ChevronDownIcon}
                    aria-controls="visible-description"
                    aria-expanded={isDescriptionVisible}
                    iconPosition="end"
                    variant="tertiary"
                    marginTop="spacing.6"
                >
                    {t(
                        `step_results.resultCard.link${
                            isDescriptionVisible ? '.readLess' : '.readMore'
                        }`,
                    )}
                </Button>
            </Box>
            {isDescriptionVisible && (
                <Box
                    color="color.content.light.default"
                    paddingTop="spacing.16"
                    id="visible-description"
                    textAlign="center"
                >
                    <Box typography="typography.body.14.regular">
                        {t(`step_results.resultCard.detailText.${type}`, {
                            replace: {
                                portal: correctBrandName[portal] ?? portal,
                            },
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default PropertyValuation;
